import React, { useState, useEffect, useRef } from 'react';
import '../assets/MultiSelectDropdown.css';

const MultiSelectDropdown = ({
  options = [],
  selected = [],
  onChange,
  placeholder = 'Select...',
  disabled = false,
  entityName = 'Items' // e.g., "Makes", "Models"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (option) => {
    const newSelected = selected.includes(option)
      ? selected.filter(item => item !== option)
      : [...selected, option];
    onChange(newSelected);
  };

  // Sanitize options to remove null/undefined values and ensure all are strings
  const sanitizedOptions = options.filter(option => {
    const isValid = option != null && typeof option === 'string' && option.trim().length > 0;
    if (!isValid && option != null) {
      console.warn(`[MultiSelectDropdown] Filtered out invalid option:`, option, `(type: ${typeof option})`);
    }
    return isValid;
  }).map(option => option.trim()); // Trim whitespace from all options

  // Log warning if null values were detected
  if (options.length !== sanitizedOptions.length) {
    console.warn(`[MultiSelectDropdown] Removed ${options.length - sanitizedOptions.length} null/invalid options from ${entityName}`);
  }

  const filteredOptions = sanitizedOptions.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDisplayText = () => {
    if (selected.length === 0) {
      return `No ${entityName}`;
    }
    if (selected.length === sanitizedOptions.length) {
      return `All ${entityName}`;
    }
    if (selected.length === 1) {
      return selected[0];
    }
    return `${selected.length} ${entityName} selected`;
  };

  // --- Start of Corrected Logic ---

  const isAllSelected = sanitizedOptions.length > 0 && selected.length === sanitizedOptions.length;
  const isPartiallySelected = selected.length > 0 && selected.length < sanitizedOptions.length;

  const handleSelectAll = () => {
    // If all are currently selected, unselect all. Otherwise, select all.
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange(sanitizedOptions.map(opt => opt)); // Pass a copy of the sanitized options array
    }
  };

  // --- End of Corrected Logic ---

  return (
    <div className={`multiselect-dropdown ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <button className="multiselect-toggle" onClick={handleToggle}>
        <span className="multiselect-display-text">{getDisplayText()}</span>
        <span className={`multiselect-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div className="multiselect-menu">
          <div className="multiselect-search-wrapper">
            <input
              type="text"
              className="multiselect-search"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <div className="multiselect-options">
            {sanitizedOptions.length > 0 && (
              <div
                className="multiselect-option select-all"
                onClick={handleSelectAll}
              >
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={el => {
                    if (el) {
                      el.indeterminate = isPartiallySelected;
                    }
                  }}
                  readOnly
                />
                <span>Select All</span>
              </div>
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option}
                  className={`multiselect-option ${selected.includes(option) ? 'selected' : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option)}
                    readOnly
                  />
                  <span>{option}</span>
                </div>
              ))
            ) : (
              <div className="multiselect-no-options">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
